/**
 * Role Modification ticket-detail view model.
 *
 * Pure functions that turn persisted workflow_state into human-first content:
 * the HALO Summary, the original request, failed-action vs evidence-source,
 * the condensed role analysis, human gate records, and human-readable errors.
 * Nothing here changes scoring or selection; it only reads what HALO persisted.
 */
import {
  WORKFLOW_STAGE,
  getWorkflowState,
  getApproval,
  formatRoleModDelta,
  getRoleModConsideredSource,
  getRoleModRolesConsidered,
} from '@/lib/workflowHelpers';

/** Transactions that report an authorization failure rather than cause it. */
const EVIDENCE_REPORT_TCODES = ['SU53', 'SU56', 'ST01', 'STAUTHTRACE', 'SUIM', 'STUSOBTRACE'];

const EVIDENCE_SOURCE_LABELS = {
  su53_screenshot: 'SU53 screenshot',
  su53: 'SU53 screenshot',
  su53_spreadsheet: 'SU53 spreadsheet export',
  sap_trace: 'SAP authorization trace',
  sap_authorization_trace: 'SAP authorization trace',
  authorization_trace: 'SAP authorization trace',
  st01: 'ST01 trace',
  normalized_input: 'Structured request text',
  trigger_workflow_upload: 'Uploaded screenshot',
};

const GATE_ORDER = [
  'qa_fut',
  'transport_release_approval',
  'qa_confirmation',
  'fut',
  'cab',
  'prod_confirmation',
];

export const ROLE_MOD_GATE_META = {
  qa_fut: {
    title: 'QA / FUT validation',
    responseLabel: 'Comment',
    decisionLabels: { passed: 'Pass', failed: 'Fail' },
    next: {
      passed: 'The CAB decision is the next step.',
      failed: 'CAB is blocked until remediation is recorded.',
    },
    note: 'FUT is performed in QS4 after the external transport process. HALO did not import to QA.',
  },
  transport_release_approval: {
    title: 'Transport release review',
    responseLabel: 'Reviewer response',
    decisionLabels: { approved: 'Approved', rejected: 'Rejected' },
    next: {
      approved: 'QA confirmation is the next manual step.',
      rejected: 'The workflow is held for manual review.',
    },
    note: 'HALO recorded the decision only. The transport was not released by HALO.',
  },
  qa_confirmation: {
    title: 'QA confirmation',
    responseLabel: 'Confirmation',
    decisionLabels: { confirmed: 'Confirmed', failed: 'Failed' },
    next: {
      confirmed: 'Functional user testing is the next step.',
      failed: 'The workflow is held for manual review.',
    },
    note: 'Manual confirmation only. HALO did not import to QA.',
  },
  fut: {
    title: 'Functional user testing',
    responseLabel: 'Requester response',
    decisionLabels: { passed: 'Passed', failed: 'Failed' },
    next: {
      passed: 'The CAB decision is the next step.',
      failed: 'CAB is blocked until remediation is recorded.',
    },
    note: null,
  },
  cab: {
    title: 'CAB decision',
    responseLabel: 'CAB response',
    decisionLabels: { approved: 'Approved', rejected: 'Rejected' },
    next: {
      approved: 'Production confirmation is the next manual step.',
      rejected: 'The change does not proceed to production.',
    },
    note: 'Human CAB decision. HALO does not auto-approve CAB.',
  },
  prod_confirmation: {
    title: 'Production confirmation',
    responseLabel: 'Basis / authorized-user response',
    decisionLabels: { confirmed: 'Confirmed', failed: 'Failed' },
    next: {
      confirmed: 'The Role Modification lifecycle is complete.',
      failed: 'The workflow is held for manual review.',
    },
    note: 'Manual confirmation only. HALO did not import to production.',
  },
};

/** Screenshot gates take images; CAB takes the decision document. */
export const ROLE_MOD_SCREENSHOT_ACCEPT = 'image/png,image/jpeg,image/webp';
export const ROLE_MOD_DOCUMENT_ACCEPT =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx';

const QA_FUT_ACTION = {
  gate: 'qa_fut',
  title: 'QA / FUT Validation',
  request:
    'The requester tests in QS4 after the transport arrives through the external process. A screenshot is required for Pass and Fail. A comment is optional. HALO does not import to QA.',
  needs: 'Pass or Fail, plus a screenshot.',
  optional: 'Comment',
  after: 'Pass continues to CAB. Fail blocks CAB and keeps the evidence and comment.',
  responseLabel: 'Comment (optional)',
  primary: ['qa_fut_pass', 'Submit QA / FUT Pass'],
  secondary: ['qa_fut_fail', 'Submit QA / FUT Fail'],
  evidenceRequired: true,
  evidenceLabel: 'Screenshot',
  evidenceAccept: ROLE_MOD_SCREENSHOT_ACCEPT,
  descriptionRequired: false,
};

const TRANSPORT_RELEASE_ACTION = {
  gate: 'transport_release_approval',
  title: 'Transport Release Approval',
  request:
    'Record whether this transport may be released. HALO stores the decision and does not release the transport in SAP.',
  needs: 'Approved or Rejected, plus a comment.',
  optional: 'Screenshot',
  after: 'Approved continues to QA confirmation. Rejected holds the workflow for manual review.',
  responseLabel: 'Comment (required)',
  primary: ['approve_transport_release', 'Approve release decision'],
  secondary: ['reject_transport_release', 'Reject release decision'],
  evidenceRequired: false,
  evidenceLabel: 'Screenshot',
  evidenceAccept: ROLE_MOD_SCREENSHOT_ACCEPT,
  descriptionRequired: true,
};

const QA_CONFIRMATION_ACTION = {
  gate: 'qa_confirmation',
  title: 'QA Confirmation (QS4)',
  request:
    'Confirm that the transport arrived in QS4 through the external process. A screenshot is required. HALO does not import to QA.',
  needs: 'Confirmed or Failed, plus a screenshot. A failure also needs a comment.',
  optional: 'Comment on a successful confirmation',
  after: 'Confirmed continues to functional user testing. Failed holds the workflow for manual review.',
  responseLabel: 'Comment',
  primary: ['confirm_qa', 'Confirm QA'],
  secondary: ['qa_failed', 'Record QA failure'],
  evidenceRequired: true,
  evidenceLabel: 'Screenshot',
  evidenceAccept: ROLE_MOD_SCREENSHOT_ACCEPT,
  descriptionRequired: false,
  secondaryDescriptionRequired: true,
};

const FUT_ACTION = {
  gate: 'fut',
  title: 'Functional User Testing (FUT)',
  request:
    'The requester tests in QS4. A screenshot is required for Pass and Fail. A comment is optional. HALO does not import to QA.',
  needs: 'Pass or Fail, plus a screenshot.',
  optional: 'Comment',
  after: 'Pass continues to CAB. Fail blocks CAB and keeps the evidence and comment.',
  responseLabel: 'Comment (optional)',
  primary: ['fut_pass', 'Submit FUT Pass'],
  secondary: ['fut_fail', 'Submit FUT Fail'],
  evidenceRequired: true,
  evidenceLabel: 'Screenshot',
  evidenceAccept: ROLE_MOD_SCREENSHOT_ACCEPT,
  descriptionRequired: false,
};

/** Form copy and action names for the gate a ticket is currently waiting on. */
export const ROLE_MOD_STAGE_ACTIONS = {
  [WORKFLOW_STAGE.AWAITING_QA_FUT]: QA_FUT_ACTION,
  [WORKFLOW_STAGE.AWAITING_QA_CONFIRMATION]: QA_CONFIRMATION_ACTION,
  [WORKFLOW_STAGE.AWAITING_FUT]: FUT_ACTION,
  [WORKFLOW_STAGE.AWAITING_TRANSPORT_RELEASE_APPROVAL]: TRANSPORT_RELEASE_ACTION,
  [WORKFLOW_STAGE.AWAITING_CAB]: {
    gate: 'cab',
    title: 'CAB Decision',
    request:
      'Record the CAB decision and a description. A meeting document is optional. Approval does not make HALO import the transport.',
    needs: 'Approved or Rejected, plus a description.',
    optional: 'CAB decision or meeting document (PDF or Word)',
    after: 'Approved continues to production confirmation. Rejected stops the workflow and keeps the decision.',
    responseLabel: 'Description (required)',
    primary: ['cab_approve', 'Approve CAB'],
    secondary: ['cab_reject', 'Reject CAB'],
    evidenceRequired: false,
    evidenceLabel: 'CAB document',
    evidenceAccept: ROLE_MOD_DOCUMENT_ACCEPT,
    descriptionRequired: true,
  },
  [WORKFLOW_STAGE.AWAITING_PROD_CONFIRMATION]: {
    gate: 'prod_confirmation',
    title: 'Production Confirmation',
    request:
      'Basis or the authorized team confirms the external production movement. A screenshot is required. HALO records the confirmation and does not import the transport.',
    needs: 'Screenshot of the production check.',
    optional: 'Comment',
    after: 'A successful confirmation completes the workflow.',
    responseLabel: 'Comment (optional)',
    primary: ['confirm_prod', 'Confirm production completion'],
    secondary: null,
    evidenceRequired: true,
    evidenceLabel: 'Screenshot',
    evidenceAccept: ROLE_MOD_SCREENSHOT_ACCEPT,
    descriptionRequired: false,
  },
};

function str(value) {
  return value == null ? '' : String(value).trim();
}

function upper(value) {
  return str(value).toUpperCase();
}

function list(value) {
  return Array.isArray(value) ? value.filter((v) => v != null && String(v).trim() !== '') : [];
}

function joinSentences(parts) {
  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export function formatWhen(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

export function formatEvidenceSource(source, fallback) {
  const key = str(source).toLowerCase();
  if (key && EVIDENCE_SOURCE_LABELS[key]) return EVIDENCE_SOURCE_LABELS[key];
  if (key) return key.replaceAll('_', ' ');
  return fallback || null;
}

function evidenceName(filename) {
  return str(filename).toLowerCase();
}

/** Label a stored file from its name and source. A spreadsheet is not called a screenshot. */
export function evidenceDisplayLabel(source, filename) {
  const name = evidenceName(filename);
  const key = str(source).toLowerCase();
  const spreadsheet = /\.(xlsx|xls|csv)$/.test(name) || key === 'su53_spreadsheet';
  const trace = key.includes('trace') || key === 'st01' || /(^|[^a-z])(trace|st01|stauth)/.test(name);
  if (trace && spreadsheet) return 'Authorization trace spreadsheet';
  if (trace) return formatEvidenceSource(source) || 'SAP authorization trace';
  if (spreadsheet) return 'SU53 spreadsheet export';
  if (/\.(png|jpe?g|webp)$/.test(name) || key === 'su53_screenshot' || key === 'su53') {
    return 'SU53 screenshot';
  }
  return formatEvidenceSource(source);
}

function proposalOf(state) {
  return state.proposal && typeof state.proposal === 'object' ? state.proposal : {};
}

function evidenceOf(state) {
  const proposal = proposalOf(state);
  return proposal.evidence || state.evidence || {};
}

function artifactOf(state) {
  const proposal = proposalOf(state);
  const artifact = proposal.evidence_artifact || state.evidence_artifact;
  return artifact && typeof artifact === 'object' && Object.keys(artifact).length ? artifact : null;
}

/**
 * Separate "what the user could not do" from "how we know".
 * SU53 (and similar) is the evidence mechanism, not the failed business action.
 */
export function getRoleModFailureContext(ticket) {
  const state = getWorkflowState(ticket);
  const evidence = evidenceOf(state);
  const artifact = artifactOf(state);
  const authObject = upper(evidence.object);
  const field = upper(evidence.field);
  const value = str(evidence.value);
  const transaction = upper(evidence.transaction);
  const transactionIsReport = !transaction || EVIDENCE_REPORT_TCODES.includes(transaction);

  let failedAction = null;
  let failedActionKind = 'unknown';
  if (authObject === 'S_TCODE' && field === 'TCD' && value) {
    failedAction = value;
    failedActionKind = 'transaction_start';
  } else if (!transactionIsReport) {
    failedAction = transaction;
    failedActionKind = 'deeper_check';
  }

  const authorizationFailure = [authObject, field, value].filter(Boolean).join(' / ') || null;
  const evidenceSource =
    evidenceDisplayLabel(evidence.evidence_source || artifact?.type, artifact?.filename) ||
    (transactionIsReport && transaction ? `${transaction} screenshot` : null);

  return {
    failedAction,
    failedActionKind,
    failedActionLabel:
      failedAction ||
      (authorizationFailure ? 'Not identified in the evidence (authorization check only)' : null),
    authorizationFailure,
    authObject: authObject || null,
    field: field || null,
    value: value || null,
    evidenceSource,
    evidenceTransaction: transactionIsReport ? transaction || null : null,
    evidenceFilename: artifact?.filename || null,
    evidenceArtifact: artifact,
  };
}

function roleModInstance(proposal, evidence) {
  const instance = str(proposal?.instance || evidence?.instance);
  if (instance) return instance;
  const system = str(proposal?.system || evidence?.system);
  const client = str(proposal?.client || evidence?.client);
  if (system && client) return `${system} / client ${client}`;
  return system || (client ? `client ${client}` : '');
}

/** The requester's own words plus the identifying facts. Never invents missing values. */
export function getRoleModOriginalRequest(ticket) {
  const state = getWorkflowState(ticket);
  const evidence = evidenceOf(state);
  const proposal = proposalOf(state);
  const artifact = artifactOf(state);
  const requestSummary = str(state.request_summary);
  let description = str(ticket?.description);
  if (!description && requestSummary) {
    const [first, ...rest] = requestSummary.split('\n');
    description = rest.length && first === str(ticket?.title) ? rest.join('\n').trim() : requestSummary;
  }
  const instance = roleModInstance(proposal, evidence);
  const manager = str(state.manager || state.extracted?.manager || ticket?.manager);
  const roleOwner = str(state.role_owner || proposal.role_owner);
  return {
    title: str(ticket?.title) || str(ticket?.ticket_number) || null,
    description: description || null,
    user: str(proposal.user || evidence.user) || null,
    instance: instance || null,
    systemClient: instance || null,
    evidenceFilename: artifact?.filename || null,
    requester:
      str(ticket?.requester_name) && str(ticket.requester_name).toLowerCase() !== 'system'
        ? str(ticket.requester_name)
        : null,
    manager: manager || null,
    roleOwner: roleOwner || null,
    submittedAt: ticket?.created_at || null,
  };
}

function businessTransactionOf(evidence) {
  const explicit = upper(evidence?.business_transaction);
  if (explicit) return explicit;
  const authObject = upper(evidence?.object);
  const field = upper(evidence?.field);
  const value = upper(evidence?.value);
  const transaction = upper(evidence?.transaction);
  if (authObject === 'S_TCODE' && field === 'TCD' && value && !EVIDENCE_REPORT_TCODES.includes(value)) {
    return value;
  }
  if (transaction && !EVIDENCE_REPORT_TCODES.includes(transaction)) return transaction;
  return '';
}

/** Functional-fit facts only. Shared-user impact and evidence transactions are omitted. */
export function fitWhyBullets(row, evidence = {}) {
  if (!row) return [];
  const factors = row.score_factors || row.scoreFactors || {};
  const breakdown = row.score_breakdown || row.scoreBreakdown || {};
  const signature = row.structural_signature || row.structuralSignature || {};
  const hasField = Boolean(
    factors.same_object_and_field ?? signature.field ?? Number(breakdown.exact_field_present) > 0,
  );
  const hasObject = Boolean(
    factors.same_object ?? signature.object ?? Number(breakdown.exact_object_present) > 0,
  );
  const currentValues = list(row.current_values || row.currentValues);
  const otherFields = list(row.other_fields_same_object || row.otherFields);
  const bullets = [];
  if (hasObject) bullets.push('requested object already exists');
  if (hasField) bullets.push('requested field already exists');
  if (hasField && currentValues.length) bullets.push(`current values exist (${currentValues.join(', ')})`);
  if (otherFields.length || Number(breakdown.other_fields_same_object) > 0) {
    bullets.push(
      otherFields.length
        ? `related fields under the object exist (${otherFields.join(', ')})`
        : 'related fields under the object exist',
    );
  }
  const business = businessTransactionOf(evidence);
  const transactionFit =
    factors.related_transaction_context === true ||
    Number(breakdown.related_transaction_context) > 0 ||
    signature.related_transaction === true;
  if (transactionFit && business) bullets.push(`business transaction ${business} is present on the role`);
  return bullets;
}

function humanReasonForRow(row, { otherAccepted = 0, humanSelection = null, evidence = {} } = {}) {
  if (!row) return null;
  const authObject = upper(evidence.object);
  const field = upper(evidence.field);
  const bullets = fitWhyBullets(row, evidence);
  const hasField = bullets.includes('requested field already exists');
  const hasObject = bullets.includes('requested object already exists');
  const currentValues = list(row.current_values || row.currentValues);

  if (!hasObject && !hasField) {
    const raw = str(row.reason || row.selection_reason || row.rejection_reason);
    return raw || null;
  }

  let sentence = hasField
    ? `It already contains authorization object ${authObject || 'the requested object'}${
        field ? ` with field ${field}` : ''
      }`
    : `It already contains authorization object ${authObject || 'the requested object'}`;
  if (hasField && currentValues.length) {
    sentence += ` and current values exist (${currentValues.join(', ')})`;
  }
  if (otherAccepted > 0 && !humanSelection?.role) {
    sentence += `, making it a stronger authorization-structure match than the ${otherAccepted} other assigned role${
      otherAccepted === 1 ? '' : 's'
    } that also contain${otherAccepted === 1 ? 's' : ''} the object`;
  }
  sentence += '.';
  if (humanSelection?.role && humanSelection.role === row.role) {
    const candidates = list(humanSelection.candidate_roles).length;
    sentence += ` It was chosen by ${humanSelection.selected_by || 'a reviewer'} after HALO found ${
      candidates || 'several'
    } equally strong candidates.`;
  }
  return sentence;
}

/** Display-only. Raw structural points stay the policy score. */
export function displayRoleFit(rawScore) {
  const raw = Number(rawScore);
  if (!Number.isFinite(raw)) return null;
  const bounded = Math.max(0, Math.min(92, raw));
  return Math.round((bounded * 100) / 92);
}

/** V3 and V2 analyses persist role_scoring_version. Older tickets have no version. */
export function getRoleModScoringModel(ticket) {
  const state = getWorkflowState(ticket);
  const version = String(
    state.role_scoring_version || proposalOf(state).role_scoring_version || '',
  ).toLowerCase();
  if (version === 'v3') {
    return { version: 'v3', modelLabel: 'V3', scoreLabel: 'Role Fit' };
  }
  if (version === 'v2') {
    return { version: 'v2', modelLabel: 'V2', scoreLabel: 'Role Fit Score' };
  }
  if (version === 'v4') {
    return { version: 'v4', modelLabel: 'V4', scoreLabel: null };
  }
  return { version: 'legacy', modelLabel: 'Legacy', scoreLabel: 'Legacy role score' };
}

export function agentContextLabel(status) {
  switch (String(status || '').toUpperCase()) {
    case 'SUPPORTED':
      return 'Supports selection';
    case 'CONTRADICTORY':
      return 'Conflicts with the automatic selection';
    case 'UNAVAILABLE':
      return 'Unavailable';
    default:
      return 'Not scored';
  }
}

/** Condensed, human-first role analysis with the full detail rows still attached. */
export function getRoleModRoleAnalysis(ticket) {
  const state = getWorkflowState(ticket);
  const stage = ticket?.workflow_stage || '';
  const evidence = evidenceOf(state);
  const rows = getRoleModRolesConsidered(ticket);
  const rawRows = getRoleModConsideredSource(state);
  const rawByRole = {};
  (Array.isArray(rawRows) ? rawRows : []).forEach((row) => {
    if (row?.role) rawByRole[row.role] = row;
  });
  const requiresSelection = stage === WORKFLOW_STAGE.ROLE_SELECTION_REQUIRED;
  const selectedName = requiresSelection
    ? ''
    : str(state.proposal?.selected_role || state.selected_role?.role);
  const selectedRow = requiresSelection
    ? null
    : rows.find((row) => row.role === selectedName) || rows.find((row) => row.selected);
  const acceptedRoles = rows.filter((row) => {
    const raw = rawByRole[row.role] || {};
    return raw.accepted === true || raw.disposition === 'selected' || row.selected;
  });
  const otherAcceptedRows = selectedRow
    ? acceptedRoles.filter((row) => row.role !== selectedRow.role)
    : acceptedRoles;
  const otherAccepted = otherAcceptedRows.length;
  const weakerAccepted = selectedRow
    ? otherAcceptedRows.filter((row) => Number(row.score) < Number(selectedRow.score)).length
    : 0;
  const scoring = getRoleModScoringModel(ticket);
  const enriched = rows.map((row) => {
    const raw = rawByRole[row.role] || {};
    const source = raw.role ? raw : row;
    const chosen = requiresSelection ? false : row.selected;
    return {
      ...row,
      selected: chosen,
      accepted: raw.accepted === true || raw.disposition === 'selected' || chosen,
      disposition: raw.disposition || (row.selected ? 'selected' : raw.accepted ? 'candidate' : 'rejected'),
      assignedUsers: list(raw.assigned_users),
      conciseReason: row.selected
        ? 'Contains the closest existing authorization structure for this request.'
        : raw.accepted
          ? 'Assigned to the user and contains related authorization context, but it is a weaker role fit.'
          : 'Assigned to the user, but does not contain the relevant authorization context.',
      fitWhy: scoring.version === 'v2' || scoring.version === 'v3' ? fitWhyBullets(source, evidence) : [],
      displayScore: scoring.version === 'v3' ? displayRoleFit(row.score) : null,
      humanReason: humanReasonForRow(source, {
        otherAccepted: row.selected ? otherAccepted : 0,
        humanSelection: state.human_role_selection,
        evidence,
      }),
    };
  });
  const totalAssigned = list(state.user_validation?.roles).length || rows.length;
  const sorted = [...enriched].sort((left, right) => {
    if (left.accepted !== right.accepted) return left.accepted ? -1 : 1;
    const scoreGap = (right.score ?? -1) - (left.score ?? -1);
    if (scoreGap !== 0) return scoreGap;
    return String(left.role).localeCompare(String(right.role));
  });
  const proposal = proposalOf(state);
  const lock = proposal.role_lock || state.role_lock || {};
  const lockLabel =
    lock.locked === true
      ? `Locked${lock.locked_by ? ` by ${lock.locked_by}` : ''}`
      : lock.locked === false
        ? 'Unlocked'
        : 'Not yet read';
  const delta =
    formatRoleModDelta(proposal.proposed_authorization_delta) ||
    (evidence.object && evidence.field && evidence.value
      ? `${evidence.object} / ${evidence.field} += ${evidence.value}`
      : null);
  const impactSource = selectedRow || {};

  return {
    requiresSelection,
    selectedRole: selectedRow?.role || null,
    selectedScore: selectedRow?.score ?? null,
    selectedReason: selectedRow
      ? humanReasonForRow(rawByRole[selectedRow.role] || selectedRow, {
          otherAccepted,
          humanSelection: state.human_role_selection,
          evidence,
        })
      : null,
    whyBullets:
      (scoring.version === 'v2' || scoring.version === 'v3') && selectedRow
        ? fitWhyBullets(rawByRole[selectedRow.role] || selectedRow, evidence)
        : [],
    displayScore: scoring.version === 'v3' ? displayRoleFit(selectedRow?.score) : null,
    rawScore: selectedRow?.score ?? null,
    agentContext: state.agent_context || proposal.agent_context || null,
    scoring,
    impact: {
      assignedUserCount:
        impactSource.assignedUserCount ??
        (proposal.assignment_count == null ? null : Number(proposal.assignment_count)),
      shared: impactSource.shared ?? proposal.shared ?? null,
      lockLabel,
      delta,
    },
    candidateRoles: requiresSelection
      ? sorted.filter((row) => row.accepted).map((row) => row.role)
      : acceptedRoles.map((row) => row.role),
    otherEvaluated: Math.max(totalAssigned - (selectedRow ? 1 : 0), 0),
    otherAccepted,
    weakerAccepted,
    humanSelection: state.human_role_selection || null,
    selectionCause: state.decision_block?.cause || null,
    rows: sorted,
  };
}

/** Human-readable error headline plus the technical evidence underneath. */
export function humanizeRoleModError(error, detail) {
  const code = str(error);
  const technical = [code, str(detail)].filter(Boolean).join(': ') || null;
  const key = code.toLowerCase();
  let headline = 'The SAP role change did not complete. No further change was made.';
  if (key === 'writes_disabled') {
    headline = 'SAP role changes are currently disabled, so no PFCG change was made.';
  } else if (key === 'fut_evidence_required') {
    headline = 'A FUT Pass needs positive evidence attached; the stage did not change.';
  } else if (key === 'unexpected_role_change') {
    headline =
      'The role changed in a way HALO did not request, so the workflow stopped for human review.';
  } else if (key.includes('data_unavailable')) {
    headline = 'SAP could not read the role’s authorization data, so nothing was changed.';
  } else if (key.includes('baseline') || key.includes('stale')) {
    headline =
      'The role changed in SAP after HALO captured its baseline, so the change was not applied.';
  } else if (key.includes('lock')) {
    headline = 'The role is locked in SAP, so HALO did not change it.';
  } else if (key.startsWith('action_not_allowed')) {
    headline = 'That action is not valid at the current workflow step.';
  } else if (key === 'qa_fut_evidence_required' || key === 'fut_evidence_required') {
    headline = 'QA / FUT needs a screenshot attached. The stage did not change.';
  } else if (key === 'cab_description_required') {
    headline = 'CAB needs a description. The stage did not change.';
  } else if (key === 'prod_evidence_required') {
    headline = 'Production confirmation needs a screenshot. The stage did not change.';
  }
  return { headline, technical };
}

function describeGateRecord(key, record) {
  if (!record || typeof record !== 'object') return null;
  const status = str(record.status).toLowerCase();
  if (!status || status === 'pending') return null;
  const meta = ROLE_MOD_GATE_META[key] || { title: key, responseLabel: 'Response', decisionLabels: {}, next: {} };
  const artifact =
    record.evidence_artifact && typeof record.evidence_artifact === 'object' && Object.keys(record.evidence_artifact).length
      ? record.evidence_artifact
      : null;
  const positive = ['approved', 'confirmed', 'passed'].includes(status);
  return {
    key,
    title: meta.title,
    status,
    positive,
    decisionLabel: meta.decisionLabels[status] || status.charAt(0).toUpperCase() + status.slice(1),
    response: str(record.description) || null,
    responseLabel: meta.responseLabel,
    actor: str(record.actor) || null,
    at: record.at || null,
    evidence: artifact,
    executionMode: record.execution_mode || record.qa_execution_mode || record.prod_execution_mode || null,
    releaseExecuted: record.release_executed,
    imported: record.qa_imported ?? record.halo_imported_prod,
    nextStep: meta.next[status] || null,
    note: meta.note,
  };
}

/** Every human decision recorded on the ticket, oldest first. */
export function getRoleModGateRecords(ticket) {
  const state = getWorkflowState(ticket);
  const approval = getApproval(ticket);
  const records = [];
  const approvalStatus = str(approval.status).toLowerCase();
  const v4 = isRoleModV4(ticket);
  const confirmRoleMode = str(approval.mode) === 'confirm_role';
  const evidenceConfirmation =
    v4 && state.evidence_confirmation && typeof state.evidence_confirmation === 'object'
    && str(state.evidence_confirmation.status) === 'confirmed'
      ? state.evidence_confirmation
      : null;
  if (evidenceConfirmation) {
    const checksById = Object.fromEntries(
      (Array.isArray(evidenceOf(state).checks) ? evidenceOf(state).checks : []).map((check) => [str(check.id), check]),
    );
    const decisions = Array.isArray(evidenceConfirmation.decisions) ? evidenceConfirmation.decisions : [];
    const named = (include) => decisions
      .filter((row) => Boolean(row?.include) === include)
      .map((row) => str(checksById[str(row.check_id)]?.object) || str(row.check_id))
      .filter(Boolean);
    const included = named(true);
    const excluded = named(false);
    const namedList = (key) => (
      Array.isArray(evidenceConfirmation[key])
        ? evidenceConfirmation[key].map(str).filter(Boolean)
        : null
    );
    const recommended = namedList('halo_recommended');
    const reviewerConfirmed = namedList('reviewer_confirmed');
    const reviewerRemoved = namedList('reviewer_removed');
    const auditDetails = [
      recommended?.length ? `HALO recommended: ${recommended.join(', ')}` : '',
      reviewerConfirmed?.length ? `Reviewer confirmed: ${reviewerConfirmed.join(', ')}` : '',
      reviewerRemoved?.length ? `Reviewer removed: ${reviewerRemoved.join(', ')}` : '',
    ].filter(Boolean);
    const artifact = artifactOf(state);
    const request = getRoleModOriginalRequest(ticket);
    records.push({
      key: 'evidence_confirmation',
      title: 'Authorization checks confirmed',
      status: 'confirmed',
      positive: true,
      decisionLabel: 'Confirmed',
      actorLabel: 'Confirmed by',
      response: str(evidenceConfirmation.comment) || null,
      responseLabel: 'Comment',
      actor: str(evidenceConfirmation.by) || null,
      at: evidenceConfirmation.at || null,
      details: auditDetails.length
        ? auditDetails
        : [
          included.length ? `Included: ${included.join(', ')}` : '',
          excluded.length ? `Left out: ${excluded.join(', ')}` : '',
        ].filter(Boolean),
      evidence: artifact,
      evidenceActor: request.requester || null,
      executionMode: null,
      nextStep: 'Role analysis was rebuilt from the confirmed checks.',
      note: null,
    });
  }
  if (v4 && confirmRoleMode && approvalStatus === 'approved') {
    const selection = state.human_role_selection && typeof state.human_role_selection === 'object' ? state.human_role_selection : {};
    const role = str(selection.role || proposalOf(state).selected_role);
    records.push({
      key: 'role_confirmed',
      title: 'Target role confirmed',
      status: 'approved',
      positive: true,
      decisionLabel: 'Confirmed',
      actorLabel: 'Confirmed by',
      response: str(approval.comment) || null,
      responseLabel: 'Comment',
      actor: str(approval.by || selection.selected_by) || null,
      at: approval.at || selection.at || null,
      details: [
        role ? `${str(approval.by || selection.selected_by) || 'The reviewer'} selected ${role} as the target role.` : '',
        str(selection.recommendation_summary),
      ].filter(Boolean),
      evidence: null,
      executionMode: null,
      nextStep: 'Change applied. HALO re-read the role, rebuilt the exact change, and saved it. No second approval step was needed.',
      note: null,
    });
  } else if (approvalStatus === 'approved' || approvalStatus === 'rejected') {
    records.push({
      key: 'approval',
      title: 'Role change approval',
      status: approvalStatus,
      positive: approvalStatus === 'approved',
      decisionLabel: approvalStatus === 'approved' ? 'Approved' : 'Rejected',
      response: str(approval.comment) || null,
      responseLabel: 'Approver comment',
      actor: str(approval.by) || null,
      at: approval.at || null,
      evidence: null,
      executionMode: null,
      nextStep:
        approvalStatus === 'approved'
          ? 'HALO applied the exact addition and prepared the transport.'
          : 'No SAP change was made.',
      note: null,
    });
  }
  GATE_ORDER.forEach((key) => {
    const record = describeGateRecord(key, state[key]);
    if (record) records.push(record);
  });
  return records;
}

/**
 * Closure recorded by HALO when no role change was needed.
 * Returns null when the ticket was not closed this way.
 */
export function getRoleModClosure(ticket) {
  const state = getWorkflowState(ticket);
  const closure = state.closure && typeof state.closure === 'object' ? state.closure : null;
  const stage = ticket?.workflow_stage || '';
  const duplicateFound = state.duplicate_grant === true || proposalOf(state).duplicate_grant === true;
  const alreadyGranted =
    closure?.reason === 'already_granted' ||
    (stage === WORKFLOW_STAGE.DONE && duplicateFound && !state.proposal);
  if (!alreadyGranted) return null;
  const roles = list(closure?.roles).length ? list(closure.roles) : list(state.duplicate_grant_roles);
  return {
    reason: 'already_granted',
    roles,
    summary: str(closure?.summary) || null,
    at: closure?.closed_at || null,
    sapChange: closure?.sap_change === true,
  };
}

/** Deterministic safety checks shown once, in one place. */
export function getRoleModSafetyChecks(ticket) {
  const state = getWorkflowState(ticket);
  const proposal = proposalOf(state);
  const duplicateRoles = list(state.duplicate_grant_roles);
  const duplicateFound = proposal.duplicate_grant === true || state.duplicate_grant === true;
  const lock = proposal.role_lock || state.role_lock || {};
  const lockKnown = typeof lock.locked === 'boolean';
  const stage = ticket?.workflow_stage || '';
  const stillReading = stage === WORKFLOW_STAGE.EXTRACTING || stage === WORKFLOW_STAGE.CHECKING;
  let lockCheck;
  if (lock.locked === true) {
    lockCheck = {
      key: 'lock',
      ok: false,
      warn: true,
      pending: false,
      label: `Role is locked${lock.locked_by ? ` by ${lock.locked_by}` : ''}`,
    };
  } else if (lockKnown) {
    lockCheck = {
      key: 'lock',
      ok: true,
      warn: false,
      pending: false,
      label: 'Role is unlocked',
    };
  } else if (stillReading) {
    lockCheck = {
      key: 'lock',
      ok: false,
      warn: false,
      pending: true,
      label: 'Role lock state not yet read',
    };
  } else {
    lockCheck = {
      key: 'lock',
      ok: false,
      warn: false,
      pending: false,
      label: duplicateFound
        ? 'Role lock was not read because no role change was proposed'
        : 'Role lock was not read',
    };
  }
  return [
    {
      key: 'duplicate',
      ok: !duplicateFound && (proposal.duplicate_grant === false || state.duplicate_grant === false),
      warn: duplicateFound,
      pending: false,
      label: duplicateFound
        ? `Exact authorization is already granted by ${duplicateRoles.join(', ') || 'an assigned role'}`
        : 'Exact authorization is not already granted by any assigned role',
    },
    lockCheck,
  ];
}

function approvedBy(approval) {
  return str(approval.by) || 'the approver';
}

/**
 * Stage-aware HALO Summary generated only from persisted workflow facts.
 * Returns paragraphs (strings) so the UI can render them without further logic.
 */
export function buildRoleModHaloSummary(ticket) {
  if (isRoleModV4(ticket)) return buildRoleModV4Summary(ticket);
  const state = getWorkflowState(ticket);
  const stage = ticket?.workflow_stage || '';
  const proposal = proposalOf(state);
  const evidence = evidenceOf(state);
  const approval = getApproval(ticket);
  const failure = getRoleModFailureContext(ticket);
  const analysis = getRoleModRoleAnalysis(ticket);
  const gates = getRoleModGateRecords(ticket);
  const gateByKey = Object.fromEntries(gates.map((row) => [row.key, row]));
  const user = str(proposal.user || evidence.user);
  const instance = roleModInstance(proposal, evidence);
  const where = instance ? ` in ${instance}` : '';
  const delta = formatRoleModDelta(proposal.proposed_authorization_delta);
  const addedValues = list(
    (proposal.proposed_authorization_delta || [])[0]?.fields?.[0]?.values,
  );
  const role = str(proposal.selected_role || state.selected_role?.role);
  const paragraphs = [];

  // 1. What failed and how HALO knows.
  if (failure.authorizationFailure && user) {
    const missing = failure.value
      ? `is missing ${failure.value} under ${failure.authObject}/${failure.field}`
      : `failed an authorization check on ${failure.authObject}`;
    const action =
      failure.failedActionKind === 'transaction_start'
        ? ` (transaction ${failure.failedAction} could not be started)`
        : failure.failedActionKind === 'deeper_check'
          ? ` while running ${failure.failedAction}`
          : '';
    paragraphs.push(
      joinSentences([
        `HALO identified that ${user} ${missing}${where}${action}.`,
        failure.evidenceSource ? `The evidence source is a ${failure.evidenceSource}.` : '',
      ]),
    );
  } else if ([WORKFLOW_STAGE.EXTRACTING, WORKFLOW_STAGE.CHECKING].includes(stage)) {
    paragraphs.push('HALO is reading the authorization evidence and the user’s assigned roles.');
  }

  // 2. What HALO discovered / decided.
  const duplicateRoles = list(state.duplicate_grant_roles);
  const closure = getRoleModClosure(ticket);
  if (closure) {
    paragraphs.push(
      `The exact authorization is already granted by ${
        closure.roles.join(', ') || 'an assigned role'
      }, so no role change was needed. HALO closed this ticket${
        closure.at ? ` on ${formatWhen(closure.at)}` : ''
      } without modifying SAP; no approval, transport, or role change occurred.`,
    );
    paragraphs.push(
      'If the action still fails for the user, the cause is outside this authorization. Raise a new ticket with fresh SU53 evidence.',
    );
    return paragraphs.filter(Boolean);
  }
  if (state.duplicate_grant === true || proposal.duplicate_grant === true) {
    paragraphs.push(
      `The exact authorization is already granted by ${
        duplicateRoles.join(', ') || 'an assigned role'
      }, so HALO did not propose a role change. A human must review why the action still fails.`,
    );
  } else if (analysis.requiresSelection) {
    paragraphs.push(
      analysis.selectionCause === 'supporting_counts'
        ? `${analysis.candidateRoles.length} assigned roles (${analysis.candidateRoles.join(
            ', ',
          )}) contain the requested field, and no business transaction separates them. Their Role Fit Scores differ by existing-value or other-field counts, which do not choose the role. A human must choose the target role. No SAP change has been made.`
        : `${analysis.candidateRoles.length} assigned roles (${analysis.candidateRoles.join(
            ', ',
          )}) have role fit scores within 5 points. HALO did not use assignment order to choose one. A human must choose the target role. No SAP change has been made.`,
    );
  } else if (role) {
    const evaluated = analysis.otherEvaluated + 1;
    const reason = analysis.selectedReason
      ? ` ${analysis.selectedReason}`
      : '';
    const lock = proposal.role_lock || state.role_lock || {};
    const lockText = lock.locked === false ? ' The role is unlocked.' : lock.locked ? ' The role is locked.' : '';
    const dupText =
      proposal.duplicate_grant === false ? ` No assigned role already grants ${failure.value || 'the value'}.` : '';
    paragraphs.push(
      joinSentences([
        `The user’s ${evaluated} assigned role${evaluated === 1 ? '' : 's'} ${
          evaluated === 1 ? 'was' : 'were'
        } evaluated and ${role} was selected.${reason}`,
        dupText,
        lockText,
      ]),
    );
    if (delta) {
      const count = proposal.assignment_count ?? list(proposal.current_assigned_users).length;
      paragraphs.push(
        `HALO proposes adding only ${addedValues.join(', ') || 'the evidenced value'} (${delta}) while leaving role assignments unchanged${
          count != null ? `; ${count} assigned user${count === 1 ? '' : 's'} ${count === 1 ? 'is' : 'are'} affected` : ''
        }.`,
      );
    }
  } else if (state.decision_block?.reason && stage === WORKFLOW_STAGE.MANUAL_REVIEW) {
    paragraphs.push(`HALO stopped for manual review: ${state.decision_block.reason}`);
  }

  // 3. Where the workflow stands.
  const approvalStatus = str(approval.status).toLowerCase();
  const applied = state.mcp_result?.role_change_applied === true || str(state.transport_status).toUpperCase() === 'APPLIED';
  const profile = state.profile_result || state.mcp_result?.profile || {};
  const transportId = str(state.transport_id || state.mcp_result?.transport_id);
  const appliedText = applied
    ? joinSentences([
        `${approvedBy(approval)} approved the change${approval.at ? ` on ${formatWhen(approval.at)}` : ''}.`,
        `HALO applied ${delta || 'the approved addition'} to ${role || 'the target role'}.`,
        profile.name
          ? `Profile ${profile.name} was ${profile.generated || profile.status === 'generated' ? 'regenerated' : profile.status || 'processed'}.`
          : '',
        transportId
          ? `Transport ${transportId} was created. HALO did not release it; release and landscape movement are handled outside HALO.`
          : '',
        state.comparison?.ok === true
          ? 'Verification confirmed only the approved values were added and assignments were preserved.'
          : '',
      ])
    : '';

  const mcpError = state.mcp_result?.error || (stage === WORKFLOW_STAGE.FAILED ? state.error : '');
  const qaFut = gateByKey.qa_fut || gateByKey.fut;
  const qa = gateByKey.qa_confirmation;
  const fut = qaFut;
  const cab = gateByKey.cab;
  const prod = gateByKey.prod_confirmation;
  const excerpt = (text) => {
    const clean = str(text);
    if (!clean) return '';
    return clean.length > 160 ? `“${clean.slice(0, 159)}…”` : `“${clean}”`;
  };

  switch (stage) {
    case WORKFLOW_STAGE.PENDING_APPROVAL:
      paragraphs.push('The change is awaiting approval. Approving applies this exact addition only; it does not grant or remove roles and does not release a transport.');
      break;
    case WORKFLOW_STAGE.ROLE_SELECTION_REQUIRED:
      paragraphs.push('Once a role is selected, HALO prepares the proposal for approval. Selection itself does not modify SAP.');
      break;
    case WORKFLOW_STAGE.REJECTED:
      paragraphs.push(`The proposal was rejected by ${approvedBy(approval)}${approval.at ? ` on ${formatWhen(approval.at)}` : ''}. No SAP change was made.`);
      break;
    case WORKFLOW_STAGE.FAILED: {
      const human = humanizeRoleModError(mcpError, state.mcp_result?.detail);
      paragraphs.push(
        joinSentences([
          approvalStatus === 'approved'
            ? `${approvedBy(approval)} approved the change, but the SAP apply did not complete.`
            : 'The workflow failed before approval.',
          human.headline,
          applied ? 'The role content change itself was applied before the failure.' : 'The role content is unchanged.',
        ]),
      );
      break;
    }
    case WORKFLOW_STAGE.AWAITING_TRANSPORT_RELEASE_APPROVAL:
      paragraphs.push(
        joinSentences([
          appliedText,
          'Waiting for a recorded transport-release decision. Recording that decision does not release the transport.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.AWAITING_QA_CONFIRMATION:
      paragraphs.push(
        joinSentences([
          appliedText,
          'Waiting for QA confirmation in QS4. A screenshot is required. HALO does not import to QA.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.AWAITING_FUT:
      paragraphs.push(
        joinSentences([
          appliedText,
          'Waiting for functional user testing in QS4. A screenshot is required.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.AWAITING_QA_FUT:
      paragraphs.push(
        joinSentences([
          appliedText,
          'Waiting for QA / FUT validation in QS4. A screenshot is required.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.FUT_REMEDIATION_REQUIRED:
    case WORKFLOW_STAGE.QA_FUT_FAILED:
      paragraphs.push(
        joinSentences([
          applied ? 'HALO applied the approved authorization change and created the transport.' : '',
          fut ? `QA / FUT was recorded as failed by ${fut.actor || 'the requester'}${fut.response ? ` ${excerpt(fut.response)}` : ''}.` : '',
          'CAB is blocked. The failure, comment, and evidence stay on the ticket.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.AWAITING_CAB:
      paragraphs.push(
        joinSentences([
          applied ? 'HALO applied the approved authorization change and the QA stage was manually confirmed.' : '',
          fut
            ? `${user || 'The requester'} completed functional testing${fut.actor ? ` (recorded by ${fut.actor})` : ''}${
                fut.evidence?.filename ? ` and supplied positive evidence (${fut.evidence.filename})` : ''
              }.`
            : '',
          'The workflow is now waiting for the CAB decision. CAB approval does not make HALO import the transport.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.CAB_REJECTED:
      paragraphs.push(
        joinSentences([
          'Functional testing passed, but',
          `CAB rejected the change${cab?.actor ? ` (${cab.actor})` : ''}${cab?.response ? ` ${excerpt(cab.response)}` : ''}.`,
          'The change does not proceed to production.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.AWAITING_PROD_CONFIRMATION:
      paragraphs.push(
        joinSentences([
          'Functional testing passed and',
          `CAB approval was recorded by ${cab?.actor || 'the CAB'}${cab?.response ? ` ${excerpt(cab.response)}` : ''}.`,
          'The workflow is waiting for production confirmation. Production movement is external. HALO will only record the confirmation.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.DONE:
      paragraphs.push(
        joinSentences([
          applied ? `HALO applied ${delta || 'the approved addition'} to ${role || 'the target role'}${transportId ? ` (transport ${transportId})` : ''}.` : '',
          cab ? `CAB approval was recorded by ${cab.actor || 'the CAB'}.` : '',
          `Production confirmation was recorded by ${prod?.actor || 'the responsible team'}${prod?.at ? ` on ${formatWhen(prod.at)}` : ''}${prod?.response ? ` ${excerpt(prod.response)}` : ''}.`,
          'The Role Modification lifecycle is complete. HALO did not release the transport and did not import it to QA or production.',
        ]),
      );
      break;
    case WORKFLOW_STAGE.MANUAL_REVIEW: {
      const held = [qaFut, qa, prod].find((row) => row && !row.positive);
      if (held) {
        paragraphs.push(
          joinSentences([
            appliedText,
            `${held.title} was recorded as ${held.decisionLabel.toLowerCase()} by ${held.actor || 'a reviewer'}${held.response ? ` ${excerpt(held.response)}` : ''}.`,
            'The workflow is held for manual review.',
          ]),
        );
      } else if (!(state.duplicate_grant === true || proposal.duplicate_grant === true)) {
        paragraphs.push('The workflow is held for manual review.');
      }
      break;
    }
    default:
      break;
  }

  return paragraphs.filter(Boolean);
}

/** Transport request, full change sentence, and role change description for the Approval card. */
export function getRoleModApprovalTransport(ticket) {
  const state = getWorkflowState(ticket);
  const proposal = proposalOf(state);
  const created = state.transport_created && typeof state.transport_created === 'object' ? state.transport_created : {};
  const id = str(created.transport_id || state.transport_id || state.mcp_result?.transport_id) || null;
  const full =
    str(created.full_change_description || state.full_change_description || state.transport_description_full) || null;
  const roleChangeDescription = str(state.role_change_description || proposal.role_change_description) || null;
  return { id, full, roleChangeDescription };
}

/** Headings the Documents card is split into, in display order. */
export const ROLE_MOD_DOCUMENT_GROUPS = [
  { key: 'ticket', title: 'Ticket', description: 'Files attached when the request was raised.' },
  { key: 'fut', title: 'FUT', description: 'QA / FUT validation screenshots from QS4.' },
  { key: 'cab', title: 'CAB', description: 'CAB decision documents.' },
  { key: 'prod', title: 'Prod', description: 'Production confirmation screenshots.' },
  { key: 'transport', title: 'Transport', description: 'Files from the older transport release step.' },
];

const GATE_DOCUMENT_GROUP = {
  approval: 'ticket',
  qa_fut: 'fut',
  qa_confirmation: 'fut',
  fut: 'fut',
  cab: 'cab',
  prod_confirmation: 'prod',
  transport_release_approval: 'transport',
};

/** How a stored file should be shown. Filename is used when the stored type is missing. */
export function roleModEvidenceKind(contentType, filename = '') {
  const type = String(contentType || '').toLowerCase();
  const name = String(filename || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/.test(name)) return 'image';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (type.includes('wordprocessingml') || name.endsWith('.docx')) return 'docx';
  if (type === 'application/msword' || name.endsWith('.doc')) return 'doc';
  if (
    type.includes('spreadsheet')
    || type.includes('ms-excel')
    || type === 'text/csv'
    || type === 'application/csv'
    || /\.(xlsx|xls|csv)$/.test(name)
  ) return 'sheet';
  if (type.startsWith('text/') || name.endsWith('.txt')) return 'text';
  return 'file';
}

function documentFromArtifact(artifact, { group, step, comment, actor, at }) {
  if (!artifact || typeof artifact !== 'object') return null;
  const filename = str(artifact.filename);
  const stored = artifact.document && typeof artifact.document === 'object' ? artifact.document : null;
  if (!filename && !stored?.path) return null;
  const contentType = str(artifact.content_type).toLowerCase();
  return {
    id: str(artifact.sha256) || `${group}:${filename}`,
    group: str(stored?.group) || group,
    step,
    filename: filename || 'evidence',
    contentType,
    kind: roleModEvidenceKind(contentType, filename),
    path: str(stored?.path) || null,
    viewable: Boolean(stored?.path),
    comment: str(comment) || null,
    actor: str(actor) || null,
    at: at || null,
  };
}

/** Every file attached to the ticket, with the comment entered alongside it. */
export function getRoleModDocuments(ticket) {
  const state = getWorkflowState(ticket);
  const request = getRoleModOriginalRequest(ticket);
  const docs = [];
  const seen = new Set();
  const push = (artifact, meta) => {
    const doc = documentFromArtifact(artifact, meta);
    if (!doc || seen.has(`${doc.group}:${doc.id}`)) return;
    seen.add(`${doc.group}:${doc.id}`);
    docs.push(doc);
  };
  const primaryArtifact = artifactOf(state);
  push(primaryArtifact, {
    group: 'ticket',
    step: evidenceDisplayLabel(
      primaryArtifact?.type || evidenceOf(state).evidence_source,
      primaryArtifact?.filename,
    ) || 'Original request',
    comment: request.description,
    actor: request.requester,
    at: request.submittedAt,
  });
  (Array.isArray(state.evidence_artifacts) ? state.evidence_artifacts : []).forEach((artifact) =>
    push(artifact, {
      group: 'ticket',
      step: evidenceDisplayLabel(artifact?.type, artifact?.filename) || 'Attachment',
      comment: null,
    }),
  );
  getRoleModGateRecords(ticket).forEach((record) =>
    push(record.evidence, {
      group: GATE_DOCUMENT_GROUP[record.key] || 'ticket',
      step: record.title,
      comment: record.response,
      actor: record.actor,
      at: record.at,
    }),
  );
  return docs;
}

const V4_REASON_TEXT = {
  eligible_roles_not_distinguishable:
    'More than one assigned role can receive the change, and the evidence does not cite one of them.',
  multiple_plausible_business_checks:
    'More than one failed check could be the requested business action.',
  authorization_instance_ambiguous:
    'More than one authorization instance could receive the value.',
  no_assigned_role_can_safely_receive_object:
    'The user has no assigned role that can be read.',
  evidence_confirmation_required:
    'Confirm which uncertain authorization checks belong to this request before role analysis.',
  needs_confirmation:
    'The evidence does not prove this check is required for the reported action, so reviewer confirmation is required.',
  human_included: 'A reviewer included this check in the authorization change.',
  human_excluded: 'A reviewer left this check out of the authorization change.',
  insufficient_evidence:
    'The evidence does not show every field the SAP authorization object requires, so this check cannot be included.',
  authorization_definition_unavailable:
    'SAP could not return the authorization object definition, so the evidence could not be checked for completeness. No change was proposed.',
  role_state_changed_since_review:
    'The role changed after the comparison was reviewed. The change was not applied. Review the rebuilt comparison and confirm a role again.',
  new_authorization_object:
    'HALO suggests this role. Selecting it would add a new authorization object.',
  NEW_ROLE_REQUIRED:
    'The client chose not to change an existing role. This workflow did not create a new role.',
  org_level_change_not_supported:
    'An included field is an org level. No partial change was proposed.',
  supporting_function_observed:
    'This supporting interface check is kept as observed context and does not block the business change.',
  informational_not_included:
    'This authorization check is informational and is not part of the authorization change.',
  evidence_unavailable: 'SU53 did not contain a usable failed authorization check.',
  trace_evidence_required:
    'SU53 did not identify an authorization object, field, and value. Submit the authorization trace as a separate ticket.',
  agent_unavailable_multiple_checks:
    'HALO cannot tell which of the failed checks belong to the requested action.',
  snapshot_hash_missing: 'The role snapshot has no hash, so no change was proposed.',
  snapshot_read_failed: 'SAP could not read the role snapshot, so no change was proposed.',
  SNAPSHOT_READ_FAILED: 'SAP could not read the role snapshot, so the approved operations were not sent.',
  SNAPSHOT_HASH_MISSING: 'The role snapshot has no hash, so the approved operations were not sent.',
  BASELINE_MISMATCH: 'The role changed after approval, so the stored operations were not sent.',
  LOCKED: 'The role is locked, so the approved operations were not sent.',
  AUTH_INSTANCE_MISMATCH: 'The named authorization instance does not match the role, so nothing was saved.',
  ORG_LEVEL_UNSUPPORTED: 'The change includes an org-level field, so nothing was saved.',
  CONCURRENT_CHANGE: 'The role changed while the update was being saved, so nothing was saved.',
  VERIFY_FAILED: 'The saved role hash does not match the snapshot read after apply.',
  v4_proposal_incomplete: 'The V4 proposal is incomplete, so nothing was sent to SAP.',
  APPLIED: 'Change applied. The confirmed authorization change was saved to the role.',
  ALREADY_PRESENT: 'The authorization was already present. HALO did not write it again.',
  APPLIED_TRANSPORT_FAILED:
    'The role change succeeded. Only transport recovery remains.',
  only_safe_assigned_role: 'This is the only assigned role that can safely receive the change.',
  role_confirmation_required: 'Confirm the one assigned role that should receive the whole change.',
  human_confirmed: 'The reviewer confirmed this role for the whole change.',
  role_description_cites_requested_action: 'The role description cites the requested action.',
  closest_structural_match:
    "This assigned role's current content is the strongest structural match for the change. Another assigned role can still be chosen.",
  transaction_gate_for_requested_action: 'This is the failed transaction for the requested action.',
  cited_in_request: 'The request names this authorization object.',
  only_failed_business_check: 'This is the only failed business check on the screenshot.',
  session_context_not_required: 'The evidence application is SESSION_MANAGER, so this row is session context.',
  same_requested_action: 'This check belongs to the requested action.',
  not_the_requested_action: 'This check is not the requested action.',
};

const PLAN_STATUS_TEXT = {
  add: 'This role already contains the authorization object. Missing values would be added.',
  mixed: 'This role would receive a missing value and a new authorization object.',
  new_object: 'Selecting this role would add a new authorization object.',
  duplicate: 'This role already contains the full condition on one authorization instance.',
  authorization_instance_ambiguous:
    'More than one authorization instance on this role could receive the value.',
  snapshot_hash_missing: 'This role did not return a snapshot hash.',
};

export function isRoleModV4(ticket) {
  const state = getWorkflowState(ticket);
  return String(state.role_scoring_version || proposalOf(state).role_scoring_version || '').toLowerCase() === 'v4';
}

export function roleModV4ReasonText(code) {
  const key = str(code);
  if (!key || /^manual review required\.?$/i.test(key)) return '';
  const known = V4_REASON_TEXT[key] || V4_REASON_TEXT[key.toLowerCase()];
  if (known) return known;
  // A stored sentence is shown as written so object names such as M_MSEG_WWE
  // keep their underscores. Only a bare reason code is humanized.
  return /\s/.test(key) ? key : key.replaceAll('_', ' ');
}

function formatAuthValue(value) {
  if (value && typeof value === 'object') {
    const low = str(value.low);
    const high = str(value.high);
    if (low && high) return `${low}–${high}`;
    return low;
  }
  return str(value);
}

// Standard SAP activity domain (TACT / ACTVT). Unknown codes stay numeric.
const ACTVT_MEANING = {
  '01': 'Create',
  '02': 'Change',
  '03': 'Display',
  '04': 'Print',
  '05': 'Lock',
  '06': 'Delete',
  '07': 'Activate',
  '08': 'Display change documents',
  '16': 'Execute',
  '22': 'Enter',
  '23': 'Maintain',
  '24': 'Archive',
  '25': 'Reload',
  '32': 'Save',
  '33': 'Read',
  '36': 'Extended maintenance',
  '43': 'Release',
  '48': 'Simulate',
};

function withActivityMeaning(field, shown) {
  if (upper(field) !== 'ACTVT' || !shown || shown.includes('–')) return shown;
  const meaning = ACTVT_MEANING[shown.padStart(2, '0')];
  return meaning ? `${shown} — ${meaning}` : shown;
}

function formatAuthFields(fields) {
  return (Array.isArray(fields) ? fields : []).map((field) => ({
    field: str(field?.field),
    values: (Array.isArray(field?.values) ? field.values : [])
      .map(formatAuthValue)
      .filter(Boolean)
      .map((shown) => withActivityMeaning(field?.field, shown)),
  })).filter((field) => field.field);
}

/** Reviewer sentence for one stored operation. S_TCODE is authorization, not the PFCG menu. */
export function describeAuthorizationChange(operation) {
  const objectName = upper(operation?.object);
  const mode = str(operation?.mode);
  if (objectName === 'S_TCODE') {
    const tcd = (Array.isArray(operation?.fields) ? operation.fields : []).find((field) => upper(field?.field) === 'TCD');
    const codes = (Array.isArray(tcd?.values) ? tcd.values : []).map(str).filter(Boolean).join(', ') || 'the requested T-code';
    if (mode === 'create_instance' || operation?.newObject === true) {
      return `A new S_TCODE authorization object was created with ${codes}.`;
    }
    return `The existing transaction authorization was extended with ${codes}.`;
  }
  if (mode === 'create_instance' || operation?.newObject === true) {
    return 'New: this authorization object is not on the selected role yet.';
  }
  return 'Existing: values are added to an authorization already on the role.';
}

function checkReasonText(check) {
  const explanation = str(check?.explanation);
  if (explanation && !/^manual review required\.?$/i.test(explanation)) return explanation;
  const reason = str(check?.reason);
  const code = str(check?.reason_code);
  if (reason && reason !== code && !/^manual review required\.?$/i.test(reason)) return reason;
  return roleModV4ReasonText(code || reason);
}

function humanReviewText(state) {
  const code = str(state.human_reason_code || state.decision_block?.code || state.decision_block?.reason);
  const summary = str(state.proposal_summary);
  const fromCode = roleModV4ReasonText(code);
  if (summary && !/^manual review required\.?$/i.test(summary) && summary !== 'Manual review required') {
    return summary;
  }
  return fromCode;
}

/**
 * Read a stored V4 decision. Returns null for every other ticket.
 * This does not call a model or rebuild operations.
 */
export function getRoleModV4View(ticket) {
  if (!isRoleModV4(ticket)) return null;
  const state = getWorkflowState(ticket);
  const proposal = proposalOf(state);
  const evidence = evidenceOf(state);
  const checks = (Array.isArray(evidence.checks) ? evidence.checks : []).map((check, index) => {
    const disposition = str(check.disposition) || 'needs_review';
    const missingFields = list(check.missing_fields).map(upper);
    const unknownFields = list(check.unknown_fields).map(upper);
    const requiredFields = list(check.required_fields).map(upper);
    const insufficient = disposition === 'insufficient_evidence';
    return {
      id: str(check.id) || `c${index + 1}`,
      application: str(check.application),
      object: str(check.object),
      resultText: str(check.result_text || check.result),
      disposition,
      insufficient,
      informational:
        check.informational === true
        || check.observed === true
        || ['informational_not_included', 'supporting_function_observed'].includes(str(check.reason_code)),
      reason: checkReasonText(check),
      fields: formatAuthFields(check.fields),
      requiredFields,
      missingFields,
      unknownFields,
      evidenceComplete: typeof check.evidence_complete === 'boolean' ? check.evidence_complete : null,
      definitionStatus: str(check.definition_status),
      // Shown on its own line so the reviewer sees exactly what the SAP
      // definition needs and the evidence does not show.
      missingLabel: insufficient && missingFields.length
        ? `Missing required fields: ${missingFields.join(', ')}`
        : insufficient && unknownFields.length
          ? `Fields not in the SAP definition: ${unknownFields.join(', ')}`
          : insufficient && str(check.definition_status) === 'not_found'
            ? 'Not an authorization object in this SAP system'
            : '',
    };
  });
  const operations = (Array.isArray(state.proposed_operations) ? state.proposed_operations : Array.isArray(proposal.operations) ? proposal.operations : []).map((operation, index) => ({
    index,
    mode: str(operation.mode),
    object: str(operation.object),
    auth: str(operation.auth),
    variant: str(operation.variant),
    fields: formatAuthFields(operation.fields),
    checkId: str(operation.check_id),
    newObject: str(operation.mode) === 'create_instance' || operation.new_object === true,
  }));
  const plans = {};
  (Array.isArray(state.role_plans) ? state.role_plans : []).forEach((plan) => {
    if (plan?.role) plans[plan.role] = str(plan.status);
  });
  const selectedRole = str(proposal.selected_role) || null;
  const assigned = (Array.isArray(state.assigned_roles) ? state.assigned_roles : []).map((row) => {
    const role = str(row?.role || row);
    const status = plans[role] || '';
    return {
      role,
      description: str(row?.description),
      selected: role === selectedRole,
      status,
      whyNot:
        role === selectedRole
          ? ''
          : selectedRole
            ? PLAN_STATUS_TEXT[status] || 'This assigned role was not selected.'
            : status
              ? PLAN_STATUS_TEXT[status] || ''
              : '',
    };
  });
  const selectionReason = str(proposal.selection_reason);
  const citation = str(proposal.selection_citation);
  const humanSelection = state.human_role_selection && typeof state.human_role_selection === 'object' ? state.human_role_selection : {};
  const selectedBy = str(humanSelection.selected_by) || (selectionReason === 'human_confirmed' ? citation : '');
  // The actual actor is named. "A person" is never shown.
  const whySelected = selectedRole
    ? selectionReason === 'human_confirmed'
      ? `${selectedBy || 'The reviewer'} selected ${selectedRole} as the target role.`
      : joinSentences([roleModV4ReasonText(selectionReason), citation ? `Citation: ${citation}.` : ''])
    : '';
  const suggestion = state.suggestion && typeof state.suggestion === 'object' ? state.suggestion : {};
  const suggestedRole = str(suggestion.suggested_role) || null;
  const comparisons = (Array.isArray(state.role_comparisons) ? state.role_comparisons : []).map((row) => ({
    role: str(row?.role),
    description: str(row?.description),
    status: str(row?.status),
    suggested: row?.suggested === true,
    recommended: row?.recommended === true || row?.suggested === true,
    recommendationSummary: str(row?.recommendation?.summary),
    recommendationReasons: list(row?.recommendation?.reasons).map(str),
    selectable: row?.selectable === true,
    why: roleModV4ReasonText(row?.why),
    assignmentCount: row?.assignment_count == null || row?.assignment_count === '' ? null : row.assignment_count,
    transactionCodes: (Array.isArray(row?.transaction_codes) ? row.transaction_codes : []).map(formatAuthValue).filter(Boolean),
    requested: (Array.isArray(row?.requested) ? row.requested : []).map((item) => {
      const placement = str(item?.placement);
      const existsOnRole = typeof item?.exists_on_role === 'boolean' ? item.exists_on_role : placement !== 'create_instance';
      return {
        checkId: str(item?.check_id),
        object: str(item?.object),
        placement,
        placementLabel:
          str(item?.placement_label)
          || (placement === 'create_instance'
            ? 'new authorization object'
            : placement === 'add_to_instance'
              ? 'existing authorization extended'
              : placement === 'duplicate'
                ? 'already present'
                : placement.replaceAll('_', ' ')),
        existsOnRole,
        missing: formatAuthFields(item?.missing),
        instances: (Array.isArray(item?.instances) ? item.instances : []).map((instance) => ({
          auth: str(instance?.auth),
          variant: str(instance?.variant),
          fields: formatAuthFields(instance?.fields),
        })),
      };
    }),
    otherAccess: (Array.isArray(row?.other_access) ? row.other_access : []).map((instance) => ({
      object: str(instance?.object),
      auth: str(instance?.auth),
      variant: str(instance?.variant),
      fields: formatAuthFields(instance?.fields),
    })),
  })).filter((row) => row.role);
  const authRaw = state.auth_instance_choice && typeof state.auth_instance_choice === 'object' ? state.auth_instance_choice : null;
  const authChoice = authRaw && authRaw.role
    ? {
        role: str(authRaw.role),
        checks: (Array.isArray(authRaw.checks) ? authRaw.checks : []).map((item) => ({
          checkId: str(item?.check_id),
          object: str(item?.object),
          instances: (Array.isArray(item?.instances) ? item.instances : []).map((instance) => ({
            auth: str(instance?.auth),
            variant: str(instance?.variant),
            fields: formatAuthFields(instance?.fields),
          })),
        })),
      }
    : null;
  const confirmation = state.evidence_confirmation && typeof state.evidence_confirmation === 'object'
    ? state.evidence_confirmation
    : null;
  const evidenceAudit = confirmation && str(confirmation.status) === 'confirmed'
    && (
      Array.isArray(confirmation.halo_recommended)
      || Array.isArray(confirmation.reviewer_confirmed)
      || Array.isArray(confirmation.reviewer_removed)
    )
    ? {
        recommended: list(confirmation.halo_recommended).map(str).filter(Boolean),
        confirmed: list(confirmation.reviewer_confirmed).map(str).filter(Boolean),
        removed: list(confirmation.reviewer_removed).map(str).filter(Boolean),
      }
    : null;
  const reviewCode = str(state.human_reason_code || state.decision_block?.code);
  const escalationRaw = state.escalation && typeof state.escalation === 'object' ? state.escalation : null;
  const escalation = escalationRaw && str(escalationRaw.status) === 'escalated'
    ? {
        gate: str(escalationRaw.gate),
        by: str(escalationRaw.by),
        at: escalationRaw.at || null,
        reason: str(escalationRaw.reason),
        summary: str(escalationRaw.summary),
        haloRecommendation: str(
          escalationRaw.halo_recommendation
          || (Array.isArray(escalationRaw.halo_recommended) ? escalationRaw.halo_recommended.join(', ') : ''),
        ),
        suggestedRole: str(escalationRaw.suggested_role),
        sapChange: false,
      }
    : null;
  const requiresEvidenceConfirmation = str(ticket.workflow_stage) === 'EVIDENCE_CONFIRMATION';
  const newRoleRequired = str(ticket.workflow_stage) === 'NEW_ROLE_REQUIRED';
  const requiresSelection = str(ticket.workflow_stage) === 'ROLE_SELECTION_REQUIRED'
    && ((Array.isArray(suggestion.candidate_roles) && suggestion.candidate_roles.length > 0) || Boolean(authChoice))
    && operations.length === 0;
  const applyStatus = str(state.sap_apply_status || state.apply_status || state.mcp_result?.sap_status).toUpperCase();
  const lock = state.pre_apply_lock || proposal.role_lock || state.role_lock || {};
  const hash = str(state.sap_snapshot_hash || proposal.sap_snapshot_hash);
  const shared = proposal.shared === true || state.shared === true;
  const assignmentCount = proposal.assignment_count ?? state.assignment_count;
  const duplicate = state.closure?.reason === 'already_granted' || applyStatus === 'ALREADY_PRESENT' || (assigned.length > 0 && assigned.every((row) => row.status === 'duplicate'));
  const orgUnsupported = reviewCode === 'org_level_change_not_supported' || applyStatus === 'ORG_LEVEL_UNSUPPORTED';
  const baselineMismatch = applyStatus === 'BASELINE_MISMATCH';
  const locked = lock.locked === true || applyStatus === 'LOCKED';
  const stage = str(ticket.workflow_stage);
  const reReview = state.re_review && typeof state.re_review === 'object' && str(state.re_review.reason)
    ? {
        reason: str(state.re_review.reason),
        role: str(state.re_review.role),
        requestedBy: str(state.re_review.requested_by),
        why: str(state.re_review.why),
        at: state.re_review.at || null,
        message: str(state.selection_error) || roleModV4ReasonText(state.re_review.reason),
      }
    : null;
  const roleStateVerified = str(state.role_state_verified?.message)
    || (hash && (selectionReason === 'human_confirmed' || applyStatus) && !baselineMismatch
      ? 'Role state verified. No changes detected since analysis.'
      : '');
  // Before a role is confirmed there is no snapshot, lock read, or change to
  // judge. Nothing is reported as missing; the checks simply have not run.
  const beforeRoleSelection = requiresEvidenceConfirmation
    || stage === 'ROLE_SELECTION_REQUIRED'
    || (!hash && !operations.length && !applyStatus && !selectedRole && !duplicate && !orgUnsupported);
  let safety;
  if (requiresEvidenceConfirmation) {
    safety = [
      { key: 'waiting', ok: false, warn: false, pending: true, label: 'Waiting for reviewer confirmation.' },
      {
        key: 'deferred',
        ok: false,
        warn: false,
        pending: true,
        label: 'Role safety checks will run after the authorization checks are confirmed.',
      },
    ];
  } else if (beforeRoleSelection) {
    safety = [
      {
        key: 'deferred',
        ok: false,
        warn: false,
        pending: true,
        label: 'Role safety checks run when a target role is confirmed: fresh re-read, lock state, duplicate check, and affected users.',
      },
    ];
  } else {
    safety = [
      {
        key: 'duplicate',
        ok: !duplicate && operations.length > 0,
        warn: duplicate,
        label: duplicate
          ? 'The full condition is already present on one authorization instance.'
          : operations.length
            ? 'The full condition is not already present on one authorization instance.'
            : 'No authorization change was built.',
      },
      {
        key: 'snapshot',
        ok: Boolean(hash) && !baselineMismatch,
        warn: !hash || baselineMismatch,
        label: baselineMismatch
          ? roleModV4ReasonText('BASELINE_MISMATCH')
          : hash
            ? roleStateVerified || 'Role state verified. No changes detected since analysis.'
            : roleModV4ReasonText('snapshot_hash_missing'),
      },
      {
        key: 'lock',
        ok: lock.locked === false && !locked,
        warn: locked,
        label: locked
          ? (str(lock.locked_by)
            ? `The role is locked by ${str(lock.locked_by)}. Close PFCG and confirm the role again.`
            : roleModV4ReasonText('LOCKED'))
          : lock.locked === false
            ? 'The role is unlocked.'
            : 'Role lock was not read.',
      },
    ];
  }
  if (!beforeRoleSelection && (shared || assignmentCount != null)) {
    safety.push({
      key: 'shared',
      ok: !shared,
      warn: shared,
      label: shared
        ? `Shared role${assignmentCount != null ? ` · ${assignmentCount} assigned users` : ''}`
        : `${assignmentCount} assigned user${Number(assignmentCount) === 1 ? '' : 's'}`,
    });
  }
  if (orgUnsupported) {
    safety.push({
      key: 'org',
      ok: false,
      warn: true,
      label: roleModV4ReasonText(applyStatus === 'ORG_LEVEL_UNSUPPORTED' ? 'ORG_LEVEL_UNSUPPORTED' : 'org_level_change_not_supported'),
    });
  }
  if (applyStatus === 'AUTH_INSTANCE_MISMATCH') {
    safety.push({
      key: 'auth',
      ok: false,
      warn: true,
      label: roleModV4ReasonText('AUTH_INSTANCE_MISMATCH'),
    });
  }
  const transportFailed = applyStatus === 'APPLIED_TRANSPORT_FAILED';
  const recommendedComparison = comparisons.find((row) => row.recommended) || null;
  const emptyState = beforeRoleSelection || stage === 'ROLE_SELECTION_REQUIRED'
    ? 'No change has been built yet. Select a target role to generate the exact authorization change.'
    : 'No authorization change was built.';
  return {
    businessAction: str(evidence.business_action),
    businessTransaction: str(evidence.business_transaction),
    checks,
    evidenceAudit,
    insufficientChecks: checks.filter((row) => row.insufficient),
    selectedRole,
    selectedBy: selectedBy || null,
    whySelected,
    roles: assigned,
    recommendedRole: suggestedRole || recommendedComparison?.role || null,
    recommendationSummary:
      str(suggestion.recommendation_summary)
      || recommendedComparison?.recommendationSummary
      || roleModV4ReasonText(suggestion.suggestion_reason),
    reReview,
    roleStateVerified,
    emptyState,
    beforeRoleSelection,
    escalation,
    humanReview: escalation || requiresSelection || requiresEvidenceConfirmation || newRoleRequired
      || reviewCode === 'role_confirmation_required'
      || reviewCode === 'evidence_confirmation_required'
      ? ''
      : (reviewCode && !selectedRole ? humanReviewText(state) : (reviewCode && !operations.length ? humanReviewText(state) : '')),
    humanReviewCode: reviewCode,
    suggestedRole,
    suggestionReason: roleModV4ReasonText(suggestion.suggestion_reason),
    suggestionCitation: str(suggestion.suggestion_citation),
    requiresSelection,
    requiresEvidenceConfirmation,
    newRoleRequired,
    comparisons,
    authChoice,
    selectionError: str(state.selection_error),
    finalReview: str(proposal.selection_reason) === 'human_confirmed' && operations.length > 0,
    assignmentCount,
    shared,
    operations,
    snapshotHash: hash,
    safety,
    apply: applyStatus
      ? {
          status: applyStatus,
          beforeHash: str(state.before_hash || state.mcp_result?.before_hash),
          afterHash: str(state.after_hash || state.mcp_result?.after_hash),
          transportId: str(state.transport_id || state.mcp_result?.transport_id),
          transportStatus: str(state.transport_status || state.mcp_result?.sap_status),
          headline: transportFailed
            ? 'The role change succeeded. Only transport recovery remains.'
            : applyStatus === 'LOCKED' && str(lock.locked_by)
              ? `The role is locked by ${str(lock.locked_by)}. Close PFCG and confirm the role again. Nothing was saved.`
              : roleModV4ReasonText(applyStatus),
          transportRecovery: transportFailed,
        }
      : null,
  };
}

function buildRoleModV4Summary(ticket) {
  const view = getRoleModV4View(ticket);
  if (!view) return [];
  const paragraphs = [];
  if (view.businessTransaction || view.businessAction) {
    paragraphs.push(
      joinSentences([
        view.businessAction ? `Requested action: ${view.businessAction}.` : '',
        view.businessTransaction ? `Transaction: ${view.businessTransaction}.` : '',
      ]),
    );
  }
  if (view.insufficientChecks.length) {
    paragraphs.push(
      view.insufficientChecks
        .map((row) => `${row.object}: insufficient evidence${row.missingLabel ? ` (${row.missingLabel.charAt(0).toLowerCase()}${row.missingLabel.slice(1)})` : ''}.`)
        .join(' ')
        + ' These checks are kept for audit and are not part of the change.',
    );
  }
  if (view.reReview) paragraphs.push(view.reReview.message);
  if (view.humanReview) paragraphs.push(view.humanReview);
  else if (view.selectedRole) {
    paragraphs.push(
      view.whySelected.startsWith(`${view.selectedRole} `) || view.whySelected.includes(` selected ${view.selectedRole} `)
        ? view.whySelected
        : joinSentences([`${view.selectedRole} was selected.`, view.whySelected]),
    );
  } else if (view.recommendedRole) {
    paragraphs.push(joinSentences([`HALO recommends ${view.recommendedRole}.`, view.recommendationSummary, 'Confirm the target role. The exact change is built from a fresh re-read and applied if the role is unchanged.']));
  } else if (view.requiresSelection) {
    paragraphs.push('Confirm which assigned role should receive the whole change.');
  }
  if (view.apply?.headline) paragraphs.push(view.apply.headline);
  else if (view.operations.length && !view.finalReview) {
    paragraphs.push(`${view.operations.length} authorization operation${view.operations.length === 1 ? '' : 's'} ${view.operations.length === 1 ? 'is' : 'are'} waiting for approval.`);
  }
  return paragraphs.filter(Boolean);
}

/** Documents grouped under the Ticket / FUT / CAB / Prod headings. Empty groups are dropped. */
export function getRoleModDocumentGroups(ticket) {
  const docs = getRoleModDocuments(ticket);
  return ROLE_MOD_DOCUMENT_GROUPS.map((group) => ({
    ...group,
    documents: docs.filter((doc) => doc.group === group.key),
  })).filter((group) => group.documents.length);
}
